import { createSelector } from '@reduxjs/toolkit';

import { Study, StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';

import { RootState } from '@/app/model/store';

import { getLatestStudyDate } from '../lib';

import { studyAdapter } from './studySlice';

export const studySelectors = studyAdapter.getSelectors(
  (state: RootState) => state.study,
);

export const { selectIds, selectEntities, selectTotal, selectById, selectAll } =
  studySelectors;

export const selectByType = (studyType: StudyType) =>
  createSelector(studySelectors.selectAll, (studies) =>
    studies.filter((study) => study.Type === studyType),
  );

export const selectByID = (studyID: string) => (state: RootState) =>
  selectById(state, studyID);

export const selectByIDs = (studyIDs: string[]) =>
  createSelector(selectEntities, (studyEntities) => {
    const studies: Study[] = [];

    studyIDs.forEach((studyID) => {
      if (studyID in studyEntities) {
        studies.push(studyEntities[studyID] as Study);
      }
    });

    return studies;
  });

export const selectStudyDateByIDs = (studyIDs: string[]) =>
  createSelector(studySelectors.selectAll, (studies) => {
    const studiesByIDs = studies.filter((study) => studyIDs.includes(study.ID));

    return getLatestStudyDate(studiesByIDs);
  });

export const selectByPatientID = (patientID: string) =>
  createSelector(studySelectors.selectAll, (studies) =>
    studies.filter((study) => study.PatientID === patientID),
  );

export const selectStudyStatus = (studyID: string) =>
  createSelector(
    studySelectors.selectAll,
    (studies) => studies.find((study) => study.ID === studyID)?.Status,
  );

export const selectNotDeletedStudiesByPatientID = (patientID: string) =>
  createSelector(selectByPatientID(patientID), (studies) =>
    studies.filter((study) => !study.Deleted?.Deleted),
  );

export const selectDentalPhotoStudies = createSelector(selectAll, (studies) =>
  studies.filter((study) => study.Type === StudyType.StudyType_DentalPhoto),
);

export const selectCBCTStudyIDByReportSourceStudyIDs = (studyIDs: string[]) =>
  createSelector(
    studySelectors.selectAll,
    (studies) =>
      studies.find(
        (study) =>
          studyIDs.includes(study.ID) &&
          study.Type === StudyType.StudyType_CBCT,
      )?.ID,
  );
